import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import {
  atualizarVinculo,
  criarVinculo,
  getVinculos,
  listarConectores,
} from "../../../services/conectorCatalogoApi";
import { conectoresDaTransportadora } from "./conectoresDaTransportadora";

// Aba admin: vínculo transportadora↔conector (TransportadoraConector). PO1-2561.
// Só aparece para admin Everlog (o backend também guarda setor==='admin').
const erroBoxStyle = { background: AUTOPILOT_COLORS.statusDangerBg, border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`, borderRadius: "4px", padding: "0.55rem 0.85rem", color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.8rem", marginBottom: "0.75rem" };
const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", padding: "0.5rem 0", textAlign: "center" };
const rowStyle = { background: AUTOPILOT_COLORS.insetSurface, borderRadius: "6px", padding: "0.55rem 0.9rem", display: "flex", alignItems: "center", justifyContent: "space-between" };

const fmtData = (v) => {
  if (!v) return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};
const hoje = () => new Date().toISOString().slice(0, 10);

export default function VinculoConectorTab({ transportadoraId }) {
  const [vinculos, setVinculos] = useState([]);
  const [conectores, setConectores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");
  const [criando, setCriando] = useState(false);
  const [novoConector, setNovoConector] = useState(""); // "key@versao"
  const [novaVigencia, setNovaVigencia] = useState("");

  const carregar = useCallback(async () => {
    if (!transportadoraId) return;
    setLoading(true);
    setErro("");
    try {
      const [vs, cs] = await Promise.all([getVinculos(transportadoraId), listarConectores()]);
      setVinculos(vs || []);
      setConectores(cs || []);
    } catch (e) {
      setErro(e?.response?.data?.error || "Falha ao carregar os vínculos (acesso admin?).");
    } finally {
      setLoading(false);
    }
  }, [transportadoraId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvarNovo = async () => {
    if (!novoConector) return setErro("Selecione um conector.");
    const [conectorKey, conectorVersao] = novoConector.split("@");
    setSaving(true);
    setErro("");
    try {
      await criarVinculo(transportadoraId, {
        conectorKey,
        conectorVersao,
        vigenciaInicio: novaVigencia || null,
      });
      setCriando(false);
      setNovoConector("");
      setNovaVigencia("");
      carregar();
    } catch (e) {
      setErro(e?.response?.data?.error || "Falha ao criar o vínculo.");
    } finally {
      setSaving(false);
    }
  };

  const encerrar = async (v) => {
    setSaving(true);
    setErro("");
    try {
      await atualizarVinculo(v.id, { vigenciaFim: hoje(), status: v.status ? 1 : 0 });
      carregar();
    } catch (e) {
      setErro(e?.response?.data?.error || "Falha ao encerrar a vigência.");
    } finally {
      setSaving(false);
    }
  };

  const alternarStatus = async (v) => {
    setSaving(true);
    setErro("");
    try {
      await atualizarVinculo(v.id, { vigenciaFim: v.vigenciaFim || null, status: v.status ? 0 : 1 });
      carregar();
    } catch (e) {
      setErro(e?.response?.data?.error || "Falha ao alterar o status.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "2rem" }}><Spinner size="sm" color="info" /></div>;
  }

  return (
    <div>
      {erro && <div style={erroBoxStyle}>{erro}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" }}>
          {vinculos.length} vínculo(s). "Vigente" = VigenciaFim vazia e Status ativo.
        </div>
        {!criando && (
          <Button size="sm" color="info" disabled={saving} onClick={() => { setErro(""); setCriando(true); }}>
            + Novo vínculo
          </Button>
        )}
      </div>

      {criando && (
        <div style={{ background: AUTOPILOT_COLORS.surface, border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: "6px", padding: "0.75rem", marginBottom: "0.75rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr auto", gap: "0.5rem", alignItems: "end" }}>
            <div>
              <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, marginBottom: "2px" }}>Conector</div>
              <Input bsSize="sm" type="select" value={novoConector} onChange={(e) => setNovoConector(e.target.value)}>
                <option value="">Selecione...</option>
                {conectoresDaTransportadora(conectores).map((c) => (
                  <option key={c.id} value={`${c.conectorKey}@${c.versao}`}>
                    {c.conectorKey} {c.versao}{c.nome ? ` — ${c.nome}` : ""}
                  </option>
                ))}
              </Input>
            </div>
            <div>
              <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, marginBottom: "2px" }}>Início (opcional)</div>
              <Input bsSize="sm" type="date" value={novaVigencia} onChange={(e) => setNovaVigencia(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <Button size="sm" color="success" disabled={saving} onClick={salvarNovo}>
                {saving ? <Spinner size="sm" /> : "Salvar"}
              </Button>
              <Button size="sm" color="secondary" outline onClick={() => setCriando(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {!vinculos.length ? (
        <div style={hintStyle}>Nenhum conector vinculado a esta transportadora.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {vinculos.map((v) => {
            const vigente = !v.vigenciaFim && v.status;
            return (
              <div key={v.id} style={rowStyle}>
                <div>
                  <div style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>
                    {v.conectorKey} {v.conectorVersao || ""}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted }}>
                    {fmtData(v.vigenciaInicio)} → {fmtData(v.vigenciaFim)}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "10px", background: vigente ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.insetSurface, color: vigente ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusWarning, border: `1px solid ${vigente ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusWarning}` }}>
                    {vigente ? "Vigente" : v.status ? "Inativo (data)" : "Desativado"}
                  </span>
                  <Button size="sm" color="secondary" outline disabled={saving} onClick={() => alternarStatus(v)}>
                    {v.status ? "Desativar" : "Ativar"}
                  </Button>
                  {!v.vigenciaFim && (
                    <Button size="sm" color="danger" outline disabled={saving} onClick={() => encerrar(v)}>
                      Encerrar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
